import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FolderOpen, Loader2, Plus, Edit } from "lucide-react";
import { Button } from "@/components/uiDir/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Badge } from "@/components/uiDir/badge";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";

interface Project {
  id: string;
  title: string;
  category: string;
  updated_at: string;
  is_private: boolean;
  featured: boolean;
}

const Projects = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate("/login");
          return;
        }

        const { data, error } = await supabase
          .from("projects")
          .select("id, title, category, updated_at, is_private, featured")
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false });

        if (error) throw error;
        setProjects((data ?? []) as Project[]);
      } catch (err) {
        console.error("Error loading projects:", err);
        toast.error("Failed to load projects");
      } finally {
        setIsLoading(false);
      }
    };

    loadProjects();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-5xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <Link to="/dashboard">
              <Button variant="ghost" className="mb-4">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-4xl font-heading font-bold mb-2">My Projects</h1>
            <p className="text-muted-foreground">Manage your portfolio projects</p>
          </div>
          <Link to="/dashboard/projects/new">
            <Button className="bg-primary text-white hover:bg-primary/90">
              <Plus className="mr-2 h-4 w-4" />
              New Project
            </Button>
          </Link>
        </div>

        {projects.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="py-12 text-center">
              <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2">No projects yet</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Add your first project to start showcasing your work.
              </p>
              <Link to="/dashboard/projects/new">
                <Button className="bg-primary text-white hover:bg-primary/90">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Project
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {projects.map((project) => (
              <Card key={project.id} className="glass-card p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <Link to={`/dashboard/projects/edit/${project.id}`}>
                        <CardTitle className="text-lg hover:underline">{project.title}</CardTitle>
                      </Link>
                      {project.featured && (
                        <Badge className="bg-primary/10 text-primary border-primary/20">Featured</Badge>
                      )}
                      <Badge variant={project.is_private ? "secondary" : "outline"}>
                        {project.is_private ? "Private" : "Public"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {project.category || "Uncategorized"} • Updated {new Date(project.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Link to={`/dashboard/projects/edit/${project.id}`}>
                    <Button variant="outline" size="sm">
                      <Edit className="h-4 w-4 mr-2" /> Edit
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
